import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Fields, Voyage } from '../core/_models'
import { useAuth, getAuth } from '../../auth'
import {
  getCreatedReportById,
  getConsumptionFuelTypes,
  getConsumptionMachineryTypes,
  getConsumptionByVoyage,
  getConsumptionByVoyageReport,
} from '../core/_requests'
import { getTanksOption } from '../../Management/core/_requests'
import { TankOptionBlock, TankOptionRow } from '../../Management/core/_models'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { capDecimalsToTwo } from '../core/valueNormalization'
import { toast } from 'react-toastify'
import {
  isFuelChangeoverReportName,
  COMMON_Q88_FUEL_GRADES,
  DEFAULT_FALLBACK_TANKS,
  TANK_ALLOCATION_STATUS_OPTIONS,
  TankWiseFuelRow,
  synthesizeTankWiseFuelRows,
} from './fuelChangeoverReportSchema'

export interface ConsumptionRequestDTO {
  reportId?: number
  legId?: number
  userId?: string | number
  companyId?: string | number
  voyageId?: number
  remarks?: string
  rows?: {
    fieldType?: string
    tankName?: string
    q88Grade?: string
    sulphurPercentage?: string
    allocationStatus?: string
    initialRob?: number
    lastRob?: number
    machineries?: { machinery?: string; operationType?: string; consumed?: number; rob?: number }[]
  }[]
  lubeOils?: {
    grade?: string
    openingStock?: string
    received?: string
    consumedByVessel?: string
    closingStock?: string
  }[]
  tanks?: TankWiseFuelRow[]
}

/* ───────────────────────── Lube Oil Status ───────────────────────── */
export interface LubeOilRow {
  grade: string
  openingStock: string
  received: string
  consumedByVessel: string
  closingStock: string
}

const LUBE_OIL_GRADE_OPTIONS = [
  'DISOLA M 4015/Gadinia s3 40/ MAK 401',
  '15W40',
  'SHELL CORENA S2P 100/ HYCOM C-100',
  'MOBIL SHC 629 / MAX SYNGEAR 150',
  'MAK AMOCAM 68/OMALA S2G 68/ MOBIL GEAR 600 XP-68',
  'AWHM-100',
] as const

const DEFAULT_MACHINERY_OPTIONS = [
  'ME', 'ME 1', 'ME 2',
  'AE', 'AE 1', 'AE 2', 'AE 3', 'AE 4',
  'Boiler', 'Boiler-1', 'Boiler-2', 'Misc',
]

const blankLubeOilRow = (): LubeOilRow => ({
  grade: '',
  openingStock: '',
  received: '',
  consumedByVessel: '',
  closingStock: '',
})

export const cleanNumberText = (v: any): string => {
  const s = String(v ?? '').trim()
  if (s === '--') return ''
  return s
}

const cleanNum = (v: any): number => {
  if (v === null || v === undefined || v === '') return 0
  const n = Number(String(v).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : 0
}

const norm = (s: any) =>
  String(s ?? '')
    .trim()
    .toLowerCase()

const isValidLubeNumericInput = (value: string) => /^\d*\.?\d{0,2}$/.test(value)

/** Truncate a typed numeric string to at most 2 decimal places (does not round). */
const clampTypedToTwoDecimals = (value: string): string => {
  const s = String(value ?? '')
  const dot = s.indexOf('.')
  return dot === -1 ? s : s.slice(0, dot + 3)
}

export const getReportVoyageId = (report: any): number => {
  if (!report) return NaN
  if (typeof report.voyage === 'number') return report.voyage
  if (typeof report.voyage?.id === 'number') return report.voyage.id
  if (typeof report.voyageDto?.id === 'number') return report.voyageDto.id
  if (typeof report.voyageId === 'number') return report.voyageId
  const raw = report.voyage?.id ?? report.voyageDto?.id ?? report.voyageId ?? report.voyage
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : NaN
}

export const getReportVesselId = (report: any): number => {
  if (!report) return NaN
  if (typeof report.vesselId === 'number') return report.vesselId
  if (typeof report.vessel?.id === 'number') return report.vessel.id
  if (typeof report.voyage?.vessel?.id === 'number') return report.voyage.vessel.id
  if (typeof report.voyageDto?.vessel?.id === 'number') return report.voyageDto.vessel.id
  if (typeof report.assignment?.vessel?.id === 'number') return report.assignment.vessel.id
  const raw =
    report.vesselId ??
    report.vessel?.id ??
    report.voyage?.vessel?.id ??
    report.voyageDto?.vessel?.id ??
    report.assignment?.vessel?.id ??
    report.vessel
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : NaN
}

export const normalizeReportStatus = (status?: string | null): string =>
  String(status ?? '')
    .trim()
    .toUpperCase()

export const toReportTime = (report: any): number => {
  const raw =
    report?.reportDateTime ??
    report?.submittedDateTime ??
    report?.createdDateTime ??
    ''
  const timestamp = new Date(raw).getTime()
  return Number.isFinite(timestamp) ? timestamp : 0
}

export const normFuelKey = (s: any): string =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

export const extractBunkerReceivedFromReport = (
  reportObj: any,
  consObj: any
): Array<{ fuelType: string; quantity: number }> => {
  const result: Array<{ fuelType: string; quantity: number }> = []

  const add = (ft: any, qty: any) => {
    const fuelType = String(ft ?? '').trim()
    const quantity = cleanNum(qty)
    if (fuelType && quantity > 0) {
      result.push({ fuelType, quantity })
    }
  }

  const parseItem = (item: any) => {
    if (!item || typeof item !== 'object') return
    const ft =
      item?.fuelType ||
      item?.fuel_type ||
      item?.grade ||
      item?.fieldType ||
      item?.fuel ||
      item?.bunkerFuel
    const qStorage = cleanNum(item?.storageQuantity ?? item?.storage_quantity ?? item?.storageQty)
    const qQty = cleanNum(item?.quantity ?? item?.qty ?? item?.amount)
    const qty = Math.max(qStorage, qQty) || qStorage || qQty
    if (ft && qty > 0) {
      add(ft, qty)
    }
  }

  // 1. From reportObj.values
  if (Array.isArray(reportObj?.values)) {
    for (const val of reportObj.values) {
      const label = norm(
        val?.field?.label ?? val?.label ?? val?.fieldLabel ?? val?.name ?? val?.fieldName ?? ''
      )
      const text = val?.valueText ?? val?.value ?? val?.text
      if (!text) continue

      if (
        label.includes('bunker received') ||
        label.includes('bunker rows') ||
        label.includes('bunker') ||
        label.includes('received rows')
      ) {
        try {
          const parsed = JSON.parse(text)
          if (Array.isArray(parsed)) {
            parsed.forEach(parseItem)
          }
        } catch { }
      } else if (label.includes("hfo (mt's)")) {
        add('HFO', text)
      } else if (label.includes("mgo (mt's)")) {
        add('MGO', text)
      } else if (typeof text === 'string' && text.trim().startsWith('[') && text.trim().endsWith(']')) {
        try {
          const parsed = JSON.parse(text)
          if (Array.isArray(parsed)) {
            parsed.forEach(parseItem)
          }
        } catch { }
      }
    }
  }

  // 2. From consObj.rows or consObj.bunkerRows if present
  if (Array.isArray(consObj?.bunkerRows)) {
    consObj.bunkerRows.forEach(parseItem)
  }

  return result
}

export const resolvePreviousReportConsumption = async (
  currentReportId: number | null,
  voyageId: number,
  vesselId: number,
  token: string = ''
) => {
  let activeToken = token
  if (!activeToken && typeof window !== 'undefined') {
    try {
      const auth = getAuth()
      activeToken =
        auth?.auth?.jwt ||
        sessionStorage.getItem('jwt') ||
        sessionStorage.getItem('token') ||
        ''
    } catch { }
  }

  if (vesselId > 0 && voyageId > 0) {
    try {
      const reports = await getCreatedReportById(vesselId)
      const sameVoyageReports = (Array.isArray(reports) ? reports : []).filter((report: any) => {
        const reportIdNum = Number(report?.id)
        const isNotCurrent = currentReportId ? reportIdNum !== currentReportId : true
        return (
          reportIdNum > 0 &&
          isNotCurrent &&
          getReportVoyageId(report) === voyageId &&
          getReportVesselId(report) === vesselId
        )
      })

      if (sameVoyageReports.length > 0) {
        const currentReport = currentReportId
          ? (Array.isArray(reports) ? reports : []).find(
            (report: any) => Number(report?.id) === currentReportId
          )
          : null
        const currentReportTime = toReportTime(currentReport)

        let candidates = sameVoyageReports
        if (currentReportTime > 0) {
          const earlierReports = sameVoyageReports.filter(
            (report: any) => toReportTime(report) > 0 && toReportTime(report) < currentReportTime
          )
          if (earlierReports.length > 0) {
            candidates = earlierReports
          }
        }

        const submittedCandidates = candidates.filter((report: any) => {
          const st = normalizeReportStatus(report?.status)
          return st !== '' && st !== 'DRAFT' && st !== 'REJECTED'
        })
        const reportList = submittedCandidates.length > 0 ? submittedCandidates : candidates

        // Sort chronologically: earliest report first to track forward evolution
        const chronologicalReports = [...reportList].sort(
          (left: any, right: any) => toReportTime(left) - toReportTime(right)
        )

        // Fetch consumption records for all reports in parallel
        const reportDetails = await Promise.all(
          chronologicalReports.map(async (r: any) => {
            try {
              const cons = await getConsumptionByVoyageReport(voyageId, Number(r.id))
              return { report: r, cons }
            } catch {
              return { report: r, cons: null }
            }
          })
        )

        const tankMap = new Map<string, any>()
        const lubeMap = new Map<string, any>()
        let latestRemarks = ''

        reportDetails.forEach(({ report, cons }: any) => {
          if (cons?.remarks && typeof cons.remarks === 'string' && cons.remarks.trim()) {
            latestRemarks = cons.remarks.trim()
          }

          const rawRows = cons?.rows || cons?.tanks || []
          rawRows.forEach((r: any) => {
            const tankNameVal = String(r.tankName || r.tank_name || '').trim()
            const fuelTypeVal = String(r.fieldType || r.fuelType || '').trim()
            const ftUpper = fuelTypeVal.toUpperCase()
            const isVlsfo = ftUpper.includes('VLSFO') || ftUpper.includes('HFO') || ftUpper.includes('HSFO')
            const isLsmgo = ftUpper.includes('LSMGO')
            const defaultTankName = isVlsfo
              ? 'HFO 1 (P) Storage Tank'
              : isLsmgo
                ? 'LSMGO Service Tank'
                : fuelTypeVal
                  ? `${fuelTypeVal} Tank`
                  : 'Tank'
            const resolvedTankName = tankNameVal || defaultTankName
            const key = tankNameVal
              ? tankNameVal.toLowerCase()
              : resolvedTankName.toLowerCase()

            const sound = cleanNum(r.initialRob ?? r.initial_rob ?? '0')
            const machList = Array.isArray(r.machineries) ? r.machineries : []
            const machConsumed = machList.reduce(
              (sum: number, m: any) => sum + cleanNum(m?.consumed),
              0
            )
            const directConsumed = cleanNum(r.consumedTransferred ?? r.consumed ?? '')
            const consumed =
              machConsumed > 0
                ? machConsumed
                : directConsumed > 0
                  ? directConsumed
                  : Math.max(sound - cleanNum(r.lastRob ?? r.rob ?? r.last_rob ?? '0'), 0)
            const lastMachRob = machList.length > 0 ? cleanNum(machList[machList.length - 1]?.rob) : 0
            const cur = cleanNum(r.lastRob ?? r.rob ?? r.last_rob ?? (lastMachRob > 0 ? lastMachRob : '0'))

            const newEntry = {
              tankName: resolvedTankName,
              q88Grade: r.q88Grade || r.q88_grade || '',
              fuelType: fuelTypeVal || r.fuelType || '',
              fieldType: fuelTypeVal || r.fieldType || '',
              sulphurPercentage: r.sulphurPercentage || r.sulphur_percentage || '',
              allocationStatus: r.allocationStatus || r.allocation_status || 'Storage Tank',
              initialRob: sound || (cur > 0 ? cur + consumed : 0),
              lastRob: cur || sound,
              rob: cur || sound,
              consumedTransferred: consumed,
              machineries: machList.length > 0 ? [...machList] : [],
            }

            if (key) tankMap.set(key, newEntry)
          })

          // Account for bunkered received fuel in this report
          const bunkeredItems = extractBunkerReceivedFromReport(report, cons)
          bunkeredItems.forEach(({ fuelType, quantity }) => {
            const bKey = normFuelKey(fuelType)
            if (!bKey) return

            let existing = tankMap.get(bKey)
            if (!existing) {
              for (const [k, entry] of tankMap.entries()) {
                const eKey = normFuelKey(entry.fuelType || entry.fieldType || entry.q88Grade || entry.tankName)
                if (eKey === bKey || eKey.includes(bKey) || bKey.includes(eKey)) {
                  existing = entry
                  break
                }
              }
            }

            if (existing) {
              const updatedLast = cleanNum(existing.lastRob) + quantity
              existing.lastRob = updatedLast
              existing.rob = updatedLast
              if (cleanNum(existing.initialRob) <= 0) {
                existing.initialRob = updatedLast
              }
            } else {
              tankMap.set(bKey, {
                tankName: `${fuelType} Storage Tank`,
                q88Grade: fuelType,
                fuelType,
                fieldType: fuelType,
                sulphurPercentage: '',
                allocationStatus: 'Storage Tank',
                initialRob: quantity,
                lastRob: quantity,
                rob: quantity,
                consumedTransferred: 0,
                machineries: [],
              })
            }
          })

          const lubeList = cons?.lubeOils || []
          if (Array.isArray(lubeList)) {
            lubeList.forEach((l: any) => {
              const gKey = norm(l.grade)
              if (!gKey) return
              const op = cleanNum(l.openingStock)
              const rec = cleanNum(l.received)
              const vesselCons = cleanNum(l.consumedByVessel)
              const cl = cleanNum(l.closingStock)

              if (!lubeMap.has(gKey)) {
                lubeMap.set(gKey, {
                  grade: l.grade,
                  openingStock: op || cl,
                  received: rec,
                  consumedByVessel: vesselCons,
                  closingStock: cl || op + rec - vesselCons,
                })
              } else {
                const existing = lubeMap.get(gKey)
                existing.received = cleanNum(existing.received) + rec
                existing.consumedByVessel = cleanNum(existing.consumedByVessel) + vesselCons
                existing.closingStock =
                  cl ||
                  cleanNum(existing.openingStock) +
                  existing.received -
                  existing.consumedByVessel
              }
            })
          }
        })

        const aggregatedRows = Array.from(tankMap.values())
        const aggregatedLube = Array.from(lubeMap.values()).map((l) => ({
          grade: l.grade,
          openingStock: String(l.openingStock),
          received: String(l.received),
          consumedByVessel: String(l.consumedByVessel),
          closingStock: String(l.closingStock),
        }))

        return {
          rows: aggregatedRows,
          tanks: synthesizeTankWiseFuelRows(aggregatedRows),
          lubeOils: aggregatedLube,
          remarks: latestRemarks,
        }
      }
    } catch (err) {
      console.warn('resolvePreviousReportConsumption error', err)
    }
  }

  try {
    return await getConsumptionByVoyage(activeToken, voyageId)
  } catch {
    return null
  }
}

export interface AddConsumptionROBRef {
  getPayload: () => { field: number; valueText: string }[]
  getDto: () => ConsumptionRequestDTO | null
}

interface Props {
  sectionTitle: string
  voyage: Voyage | null
  reportId?: number | null
  legId?: number | null
  fields: Fields[]
  onChange: (label: string, id: number, value: string, isUserInitiated?: boolean) => void
  values?: { [fieldId: number]: { value: string; label: string } }
  snapshot?: ConsumptionRequestDTO | null
  onDtoChange?: (dto: ConsumptionRequestDTO | null, isUserInitiated?: boolean) => void
  isFuelChangeover?: boolean
  reportName?: string
  refreshFlag?: number
}

interface MachineryRow {
  fieldId: number
  value: string
  operationType?: string
  remarks?: string
  consumedId: number
  consumed: string
  robId: number
  rob: string
}

interface OuterRow {
  fuelTypeId: number
  fuelType: string
  tankName: string
  q88Grade: string
  sulphurPercentage: string
  allocationStatus: string
  initialRobId: number
  initialRob: string
  robId: number // Last ROB
  rob: string
  remarks: string
  machineries: MachineryRow[]
}

export interface TankConfigOption {
  name: string
  defaultGrade: string
  fuelType: string
  status: string
}

const AddConsumptionROB = forwardRef<AddConsumptionROBRef, Props>(
  (
    {
      sectionTitle,
      voyage,
      reportId,
      legId,
      fields,
      onChange,
      values,
      snapshot,
      onDtoChange,
      refreshFlag,
      isFuelChangeover: isFuelChangeoverProp,
      reportName,
    },
    ref
  ) => {
    const isFuelChangeover = Boolean(
      isFuelChangeoverProp || isFuelChangeoverReportName(reportName)
    )
    const authCtx: any = useAuth()
    const token =
      authCtx?.auth?.auth?.jwt ||
      authCtx?.auth?.jwt ||
      sessionStorage.getItem('jwt') ||
      sessionStorage.getItem('token') ||
      ''

    const currentUser = authCtx?.currentUser

    const userId = String(currentUser?.id ?? '0')
    const companyId = String(
      currentUser?.company?.id ??
      currentUser?.companyId ??
      currentUser?.companyAdminId ??
      currentUser?.companyAdmin?.id ??
      currentUser?.companyGroupAdminId ??
      currentUser?.companyGroupAdmin?.id ??
      '0'
    )

    const [fuelOptionsApi, setFuelOptionsApi] = useState<string[]>([])
    const [machineryOptionsApi, setMachineryOptionsApi] = useState<string[]>([])
    const [vesselTanks, setVesselTanks] = useState<TankConfigOption[]>([...DEFAULT_FALLBACK_TANKS])
    const [previousRobMap, setPreviousRobMap] = useState<
      Record<string, { initial: string; last: string }>
    >({})
    const [previousLubeMap, setPreviousLubeMap] = useState<Record<string, string>>({})
    const [optionsLoading, setOptionsLoading] = useState(true)
    const hasHydratedRef = useRef(false)
    const isUserModifiedRef = useRef(false)
    const [previousSubmittedData, setPreviousSubmittedData] = useState<any | null>(null)
    const previousSubmittedDataRef = useRef<any>(null)
    const [remarks, setRemarks] = useState('')

    // -------- helpers --------
    const norm = (s: any) =>
      String(s ?? '')
        .trim()
        .toLowerCase()

    const isInitialRobLabel = (label: any) => {
      const l = norm(label)
      return (
        l.includes('initial rob') ||
        l.includes('inital rob') ||
        (l.includes('opening') && l.includes('rob'))
      )
    }

    const smartParse = (raw: any) => {
      if (raw === undefined || raw === null || raw === '') return raw
      if (typeof raw !== 'string') return raw
      try {
        return JSON.parse(raw)
      } catch {
        return raw
      }
    }

    const toStringArray = (raw: any): string[] => {
      const v = smartParse(raw)
      if (v === undefined || v === null || v === '') return []
      if (Array.isArray(v)) return v.map((x) => String(x))
      return [String(v)]
    }

    const toString2D = (raw: any): string[][] => {
      const v = smartParse(raw)
      if (!Array.isArray(v)) return []
      return v.map((row: any) => (Array.isArray(row) ? row.map(String) : [String(row)]))
    }

    const fuelKey = (fuel: string) => norm(fuel)

    const normalizeFuelTypeKey = (fuel: any) =>
      String(fuel ?? '')
        .trim()
        .toUpperCase()
        .replace(/\s+/g, ' ')

    const firstNonEmptyNumberText = (...candidates: any[]) => {
      for (const c of candidates) {
        const val = cleanNumberText(c)
        if (val !== '') return val
      }
      return ''
    }

    const rowHasMeaningfulMachineryData = (row?: OuterRow) =>
      Boolean(
        row?.machineries?.some((machinery) => {
          const machineryName = String(machinery?.value ?? '').trim()
          const consumed = cleanNumberText(machinery?.consumed)
          return machineryName !== '' || (consumed !== '' && consumed !== '0')
        })
      )

    // -------- field map --------
    const fieldMap = useMemo(() => {
      const makeEmpty = (label: string): Fields => ({
        id: -1,
        label,
        fieldType: 'unknown',
        readOnly: false,
        isActive: false,
        required: false,
        submenuId: -1,
        optionsJson: [],
      })

      const findFirst = (pred: (f: Fields) => boolean, fallback: string) =>
        fields.find(pred) ?? makeEmpty(fallback)

      const findNth = (pred: (f: Fields) => boolean, n: number, fallback: string) => {
        const matches = fields.filter(pred)
        return matches[n - 1] ?? makeEmpty(fallback)
      }

      const isInitialRob = (f: Fields) => isInitialRobLabel(f.label)

      const isRobField = (f: Fields) => {
        const l = norm(f.label)
        return l.includes('rob') && !isInitialRobLabel(l)
      }

      return {
        fuelTypeField: findFirst((f) => norm(f.label).includes('fuel type'), 'Fuel Type'),
        initialRobField: findFirst(isInitialRob, 'Initial ROB'),
        outerRobField: findNth(isRobField, 1, 'ROB [outer]'),
        innerRobField: findNth(isRobField, 2, 'ROB [inner]'),
        machineryField: findFirst((f) => norm(f.label).includes('machinery'), 'Machinery'),
        operationTypeField: findFirst(
          (f) => norm(f.label).includes('operation type'),
          'Operation Type'
        ),
        consumedField: findFirst((f) => norm(f.label).includes('consumed'), 'Consumed'),
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields])

    const {
      fuelTypeField,
      initialRobField,
      outerRobField,
      innerRobField,
      machineryField,
      operationTypeField,
      consumedField,
    } = fieldMap

    const fuelOptions: string[] =
      fuelOptionsApi.length > 0 ? fuelOptionsApi : (fuelTypeField.optionsJson as string[]) || []

    const configuredMachineryOptions = Array.isArray(machineryField.optionsJson)
      ? machineryField.optionsJson as string[]
      : []
    const machineryOptions: string[] = Array.from(new Set([
      ...DEFAULT_MACHINERY_OPTIONS,
      ...configuredMachineryOptions,
      ...machineryOptionsApi,
    ]))

    const availableTankOptions = useMemo(() => {
      const map = new Map<string, TankConfigOption>()
      vesselTanks.forEach((t) => map.set(t.name, t))
      DEFAULT_FALLBACK_TANKS.forEach((t) => {
        if (!map.has(t.name)) map.set(t.name, t)
      })
      return Array.from(map.values())
    }, [vesselTanks])

    const makeEmptyRow = (defaultTank?: TankConfigOption): OuterRow => {
      const gradeMatch = defaultTank?.defaultGrade
        ? COMMON_Q88_FUEL_GRADES.find((g) => g.grade === defaultTank.defaultGrade)
        : undefined

      return {
        fuelTypeId: fuelTypeField.id,
        fuelType: defaultTank?.fuelType || '',
        tankName: defaultTank?.name || '',
        q88Grade: defaultTank?.defaultGrade || '',
        sulphurPercentage: gradeMatch?.sulphur || '',
        allocationStatus: defaultTank?.status || 'Storage Tank',
        initialRobId: initialRobField.id,
        initialRob: '',
        robId: outerRobField.id,
        rob: '0',
        remarks: '',
        machineries: [
          {
            fieldId: machineryField.id,
            value: '',
            operationType: '',
            consumedId: consumedField.id,
            consumed: '',
            robId: innerRobField.id,
            rob: '',
          },
        ],
      }
    }

    const [rows, setRows] = useState<OuterRow[]>([makeEmptyRow()])

    // Fetch vessel-specific tanks if configured
    useEffect(() => {
      const fetchTanks = async () => {
        const vesselId = Number(voyage?.vessel?.id ?? (voyage as any)?.vesselId ?? 0)
        if (!vesselId || vesselId <= 0) return
        try {
          const res = await getTanksOption(vesselId)
          if (res && Array.isArray(res.blocks) && res.blocks.length > 0) {
            const tanks: TankConfigOption[] = []
            res.blocks.forEach((block: TankOptionBlock) => {
              const rowList = [...(block.fuelRows || []), ...(block.dieselRows || [])]
              rowList.forEach((r: TankOptionRow) => {
                const name = String(r.tankName || '').trim()
                if (name) {
                  const grade = String(r.grade || '').trim()
                  const gradeMatch = COMMON_Q88_FUEL_GRADES.find(
                    (g) =>
                      g.grade.toLowerCase() === grade.toLowerCase() ||
                      g.fuelType.toLowerCase() === grade.toLowerCase()
                  )
                  tanks.push({
                    name,
                    defaultGrade: gradeMatch?.grade || grade || 'VLSFO 0.5% S (RMG 380)',
                    fuelType: gradeMatch?.fuelType || 'VLSFO',
                    status: name.toLowerCase().includes('service')
                      ? 'In Service (Pre-changeover)'
                      : name.toLowerCase().includes('settling')
                        ? 'Settling / Standby'
                        : name.toLowerCase().includes('eca')
                          ? 'ECA Storage Tank'
                          : 'Storage Tank',
                  })
                }
              })
            })
            if (tanks.length > 0) {
              setVesselTanks(tanks)
            }
          }
        } catch {
          // fallback to DEFAULT_FALLBACK_TANKS
        }
      }
      fetchTanks()
    }, [voyage?.vessel?.id, (voyage as any)?.vesselId])

    // -------- Lube Oil Status --------
    const [lubeRows, setLubeRows] = useState<LubeOilRow[]>([blankLubeOilRow()])
    const [customLubeRows, setCustomLubeRows] = useState<Record<number, boolean>>({})

    const computeLubeClosing = (row: LubeOilRow): string => {
      const parse = (v: string) => {
        const num = parseFloat(v)
        return Number.isNaN(num) ? 0 : num
      }
      const hasAny =
        String(row.openingStock).trim() !== '' ||
        String(row.received).trim() !== '' ||
        String(row.consumedByVessel).trim() !== ''
      if (!hasAny) return ''
      const closing = parse(row.openingStock) + parse(row.received) - parse(row.consumedByVessel)
      return capDecimalsToTwo(closing)
    }

    const handleLubeChange = (index: number, field: keyof LubeOilRow, value: string) => {
      if (field !== 'grade' && value !== '' && !isValidLubeNumericInput(value)) return
      isUserModifiedRef.current = true
      setLubeRows((prev) => {
        const copy = [...prev]
        const updated: LubeOilRow = { ...copy[index], [field]: value }
        if (field === 'grade') {
          const prevClosing = previousLubeMap[norm(value)]
          if (prevClosing !== undefined && prevClosing !== '') {
            updated.openingStock = prevClosing
            updated.closingStock = computeLubeClosing(updated)
          }
        }
        if (field === 'openingStock' || field === 'received' || field === 'consumedByVessel') {
          const closing = computeLubeClosing(updated)
          if (closing !== '' && parseFloat(closing) < 0) {
            toast.warn('Lube oil closing stock cannot be negative.', {
              position: 'top-center',
              toastId: 'consumption-lube-negative',
            })
            updated[field] = ''
            updated.closingStock = computeLubeClosing(updated)
          } else {
            updated.closingStock = closing
          }
        }
        copy[index] = updated
        return copy
      })
    }

    const addLubeRow = () => {
      isUserModifiedRef.current = true
      setLubeRows((prev) => [...prev, blankLubeOilRow()])
    }
    const removeLubeRow = (index: number) => {
      isUserModifiedRef.current = true
      setLubeRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev))
      setCustomLubeRows((prev) => {
        const next: Record<number, boolean> = {}
        Object.keys(prev).forEach((keyStr) => {
          const k = Number(keyStr)
          if (k < index) {
            if (prev[k]) next[k] = true
          } else if (k > index) {
            if (prev[k]) next[k - 1] = true
          }
        })
        return next
      })
    }

    const getLubeGradeOptionsForRow = (rowsArr: LubeOilRow[], rowIndex: number) => {
      const selected = (rowsArr[rowIndex]?.grade ?? '').trim()
      const taken = new Set(
        rowsArr.filter((_, i) => i !== rowIndex).map((r) => (r.grade ?? '').trim())
      )
      return LUBE_OIL_GRADE_OPTIONS.filter((opt) => opt === selected || !taken.has(opt))
    }

    const getLubeList = (obj: any): any[] => {
      if (!obj || typeof obj !== 'object') return []
      if (Array.isArray(obj.lubeOils) && obj.lubeOils.length > 0) return obj.lubeOils
      if (Array.isArray(obj.lube_oils) && obj.lube_oils.length > 0) return obj.lube_oils
      if (Array.isArray(obj.lubes) && obj.lubes.length > 0) return obj.lubes
      if (Array.isArray(obj.lubeOil) && obj.lubeOil.length > 0) return obj.lubeOil
      return []
    }

    const buildLubeRowsFromApi = (list: any): LubeOilRow[] => {
      if (!Array.isArray(list) || list.length === 0) return [blankLubeOilRow()]
      const built = list.map((r: any) => {
        const grade = String(r?.grade ?? r?.lubeGrade ?? r?.lube_grade ?? '').trim()
        const openingStock = capDecimalsToTwo(
          cleanNumberText(r?.openingStock ?? r?.opening_stock ?? r?.opening ?? '')
        )
        const received = capDecimalsToTwo(
          cleanNumberText(r?.received ?? r?.received_qty ?? r?.receivedQty ?? '')
        )
        const consumedByVessel = capDecimalsToTwo(
          cleanNumberText(
            r?.consumedByVessel ?? r?.consumed_by_vessel ?? r?.consumedVessel ?? r?.consumed ?? ''
          )
        )
        const closingStockRaw = capDecimalsToTwo(
          cleanNumberText(r?.closingStock ?? r?.closing_stock ?? r?.closing ?? '')
        )

        const tempRow: LubeOilRow = {
          grade,
          openingStock,
          received,
          consumedByVessel,
          closingStock: closingStockRaw,
        }

        const calculatedClosing = computeLubeClosing(tempRow)
        return {
          ...tempRow,
          closingStock: closingStockRaw || calculatedClosing,
        }
      })
      return built.length > 0 ? built : [blankLubeOilRow()]
    }

    const applyLubeRows = (list: any) => {
      const built = buildLubeRowsFromApi(list)
      setLubeRows(built)
      const customs: Record<number, boolean> = {}
      built.forEach((row, idx) => {
        const g = String(row.grade ?? '').trim()
        if (
          g &&
          (!LUBE_OIL_GRADE_OPTIONS.includes(g) ||
            g.toLowerCase() === 'others' ||
            g.toLowerCase() === 'other')
        ) {
          customs[idx] = true
        }
      })
      setCustomLubeRows(customs)
    }

    const hasMeaningfulSnapshot = (dto?: ConsumptionRequestDTO | null) => {
      const snapshotRows = dto?.rows ?? []
      const hasMeaningfulRows = snapshotRows.some(
        (row) =>
          String(row?.fieldType ?? row?.tankName ?? '').trim() !== '' &&
          (Number(row?.initialRob ?? 0) > 0 ||
            Number(row?.lastRob ?? 0) > 0 ||
            (Array.isArray(row?.machineries) &&
              row.machineries.some(
                (m) => Number(m?.consumed ?? 0) > 0 || Number(m?.rob ?? 0) > 0
              )))
      )
      const hasMeaningfulLube =
        Array.isArray(dto?.lubeOils) &&
        dto!.lubeOils!.some(
          (l) =>
            String(l?.grade ?? '').trim() !== '' ||
            Number(l?.openingStock ?? 0) > 0 ||
            Number(l?.closingStock ?? 0) > 0
        )
      return hasMeaningfulRows || hasMeaningfulLube || (Array.isArray(dto?.tanks) && dto!.tanks!.length > 0)
    }

    const buildRowsFromDto = (dto?: ConsumptionRequestDTO | null): OuterRow[] => {
      const mappedRows = dto?.rows ?? []
      if (mappedRows.length === 0) return [makeEmptyRow()]

      return mappedRows.map((r) => {
        const machList = Array.isArray(r?.machineries) ? r.machineries : []
        const machListForUi = machList.length ? machList : [{}]

        return {
          fuelTypeId: fuelTypeField.id,
          fuelType: String(r?.fieldType ?? ''),
          tankName: String(r?.tankName ?? ''),
          q88Grade: String(r?.q88Grade ?? ''),
          sulphurPercentage: String(r?.sulphurPercentage ?? ''),
          allocationStatus: String(r?.allocationStatus ?? ''),
          initialRobId: initialRobField.id,
          initialRob: cleanNumberText(r?.initialRob ?? ''),
          robId: outerRobField.id,
          rob: cleanNumberText(r?.lastRob ?? '0') || '0',
          remarks: '',
          machineries: machListForUi.map((m: any) => ({
            fieldId: machineryField.id,
            value: String(m?.machinery ?? ''),
            operationType: String(m?.operationType ?? ''),
            consumedId: consumedField.id,
            consumed: cleanNumberText(m?.consumed ?? ''),
            robId: innerRobField.id,
            rob: cleanNumberText(m?.rob ?? ''),
          })),
        }
      })
    }

    const mapApiRowsToUiRows = (apiRows: any[]): OuterRow[] => {
      if (!Array.isArray(apiRows) || apiRows.length === 0) return [makeEmptyRow()]

      return apiRows.map((r: any) => {
        const machList = Array.isArray(r?.machineries) ? r.machineries : []
        const lastFromMachinery = machList.length ? machList[machList.length - 1]?.rob : undefined
        const init = cleanNumberText(r?.initialRob ?? r?.initial_rob ?? '')
        const last =
          cleanNumberText(lastFromMachinery ?? r?.lastRob ?? r?.rob ?? r?.last_rob ?? '0') || '0'

        const machListForUi = machList.length ? machList : [{}]

        return {
          fuelTypeId: fuelTypeField.id,
          fuelType: String(r?.fieldType ?? r?.fuelType ?? ''),
          tankName: String(r?.tankName ?? r?.tank_name ?? ''),
          q88Grade: String(r?.q88Grade ?? r?.q88_grade ?? ''),
          sulphurPercentage: String(r?.sulphurPercentage ?? r?.sulphur_percentage ?? ''),
          allocationStatus: String(r?.allocationStatus ?? r?.allocation_status ?? ''),
          initialRobId: initialRobField.id,
          initialRob: capDecimalsToTwo(cleanNumberText(String(init ?? ''))),
          robId: outerRobField.id,
          rob: capDecimalsToTwo(cleanNumberText(String(last ?? '0'))) || '0',
          remarks: String(r?.remarks ?? ''),
          machineries: machListForUi.map((m: any) => ({
            fieldId: machineryField.id,
            value: String(m?.machinery ?? ''),
            operationType: String(m?.operationType ?? ''),
            consumedId: consumedField.id,
            consumed: capDecimalsToTwo(cleanNumberText(m?.consumed ?? m?.consumption ?? '')),
            robId: innerRobField.id,
            rob: capDecimalsToTwo(cleanNumberText(m?.rob ?? '')),
          })),
        }
      })
    }

    const previousClosingRobForRow = (row?: OuterRow): string => {
      if (!row) return ''
      return firstNonEmptyNumberText(
        row.rob,
        row.machineries?.[row.machineries.length - 1]?.rob,
        row.initialRob,
        '0'
      )
    }

    const isRowMatch = (rowA: any, rowB: any): boolean => {
      const tankA = String(rowA?.tankName ?? rowA?.tank_name ?? '').trim().toLowerCase()
      const tankB = String(rowB?.tankName ?? rowB?.tank_name ?? '').trim().toLowerCase()
      const fuelA = normFuelKey(rowA?.fuelType ?? rowA?.fieldType ?? '')
      const fuelB = normFuelKey(rowB?.fuelType ?? rowB?.fieldType ?? '')

      if (tankA && tankB) {
        return tankA === tankB
      }
      if (fuelA && fuelB && fuelA === fuelB) {
        return true
      }
      return false
    }

    const mergeRowsWithCarryForward = (
      currentRows: OuterRow[],
      carryForwardRows: OuterRow[]
    ): OuterRow[] => {
      const result: OuterRow[] = currentRows.map((row) => ({
        ...row,
        machineries:
          row.machineries?.length > 0 ? row.machineries : [makeEmptyRow().machineries[0]],
      }))

      const matchedCarryIndices = new Set<number>()

      // 1. For each row already in result, look for a matching carryRow to seed initialRob if needed
      result.forEach((row, rIdx) => {
        const carryIdx = carryForwardRows.findIndex(
          (cRow, cIdx) => !matchedCarryIndices.has(cIdx) && isRowMatch(row, cRow)
        )
        if (carryIdx !== -1) {
          matchedCarryIndices.add(carryIdx)
          const carryRow = carryForwardRows[carryIdx]
          const existingInitial = cleanNumberText(row.initialRob)
          const nextInitialRob = existingInitial || previousClosingRobForRow(carryRow)
          const existingRob = cleanNumberText(row.rob)
          const nextMachineries =
            row.machineries?.length > 0
              ? row.machineries
              : carryRow.machineries?.length > 0
                ? carryRow.machineries
                : [makeEmptyRow().machineries[0]]

          result[rIdx] = {
            ...row,
            fuelType: row.fuelType || carryRow.fuelType,
            tankName: row.tankName || carryRow.tankName,
            q88Grade: row.q88Grade || carryRow.q88Grade,
            sulphurPercentage: row.sulphurPercentage || carryRow.sulphurPercentage,
            allocationStatus: row.allocationStatus || carryRow.allocationStatus,
            initialRob: nextInitialRob,
            rob: existingRob || nextInitialRob || '0',
            machineries: nextMachineries,
          }
        }
      })

      // 2. For any carryRow not matched and not present in result, append as a new row
      carryForwardRows.forEach((carryRow, cIdx) => {
        if (matchedCarryIndices.has(cIdx)) return
        if (result.some((r) => isRowMatch(r, carryRow))) return

        const carryInitial = previousClosingRobForRow(carryRow)
        result.push({
          ...carryRow,
          initialRob: carryInitial,
          rob: carryInitial || '0',
          machineries:
            carryRow.machineries?.length > 0
              ? carryRow.machineries
              : [makeEmptyRow().machineries[0]],
        })
      })

      const finalRows = result.map((r) => applyConsumptionMath(r))
      return finalRows.length > 0 ? finalRows : [makeEmptyRow()]
    }

    // Build synthesized TankWiseFuelRow list for Fuel Changeover read-only telemetry
    const synthesizedTankWiseRows = useMemo((): TankWiseFuelRow[] => {
      const submitted =
        previousSubmittedData?.rows?.length
          ? previousSubmittedData.rows
          : snapshot?.rows?.length
            ? snapshot.rows
            : previousSubmittedDataRef.current?.rows || []
      return synthesizeTankWiseFuelRows(submitted, availableTankOptions)
    }, [previousSubmittedData, snapshot, availableTankOptions])

    const changeoverKpis = useMemo(() => {
      let totalVlsfo = 0
      let totalLsmgo = 0
      let totalConsumed = 0
      let activeServiceTankSulphur = ''
      let isEcaCompliant = false
      const hasData = synthesizedTankWiseRows.length > 0

      synthesizedTankWiseRows.forEach((r) => {
        const cur = Number(r.currentRob) || 0
        const cons = Number(r.consumedTransferred) || 0
        totalConsumed += cons
        const isLsmgo =
          r.fuelType === 'LSMGO' ||
          r.fuelType === 'MGO' ||
          r.fuelType === 'ULSFO' ||
          r.q88Grade.includes('0.1%') ||
          r.tankName.toUpperCase().includes('LSMGO') ||
          (parseFloat(r.sulphurPercentage) <= 0.10 && parseFloat(r.sulphurPercentage) > 0)

        if (isLsmgo) {
          totalLsmgo += cur
        } else {
          totalVlsfo += cur
        }

        if (
          r.allocationStatus.toLowerCase().includes('active') ||
          r.allocationStatus.toLowerCase().includes('in service') ||
          r.tankName.toLowerCase().includes('service')
        ) {
          activeServiceTankSulphur = r.sulphurPercentage
          const sNum = parseFloat(r.sulphurPercentage)
          if (Number.isFinite(sNum) && sNum <= 0.10) {
            isEcaCompliant = true
          }
        }
      })

      return {
        totalVlsfo,
        totalLsmgo,
        totalConsumed,
        activeServiceTankSulphur:
          activeServiceTankSulphur ||
          (totalLsmgo > 0 ? '0.09%' : totalVlsfo > 0 ? '0.48%' : '--'),
        isEcaCompliant,
        hasData,
      }
    }, [synthesizedTankWiseRows])

    // ✅ Build DTO from current rows (independent of dynamic fields)
    const buildDtoFromRows = (copy: OuterRow[]): ConsumptionRequestDTO | null => {
      if (!voyage?.id) return null

      const parsedUserId = Number(userId)
      const parsedCompanyId = Number(companyId)

      const mappedRows = copy
        .filter((r) => !!String(r.fuelType ?? r.tankName ?? '').trim())
        .map((r) => {
          const key = fuelKey(String(r.fuelType ?? ''))
          const tankKey = normFuelKey(r.tankName)
          const prev = (tankKey && previousRobMap[tankKey]) || previousRobMap[key]
          const providedInitial = parseNumberSafe(r.initialRob)
          const prevInitial = parseNumberSafe(prev?.initial)
          const prevLast = parseNumberSafe(prev?.last)
          const firstMachRob = parseNumberSafe(r.machineries?.[0]?.rob)
          const firstMachConsumed = parseNumberSafe(r.machineries?.[0]?.consumed)
          const lastMachRob = parseNumberSafe(r.machineries?.[r.machineries.length - 1]?.rob)
          const derivedFromMach =
            firstMachRob !== null && firstMachConsumed !== null
              ? firstMachRob + firstMachConsumed
              : null

          const machConsumedTotal = (r.machineries || []).reduce((sum, m) => {
            const c = parseNumberSafe(m.consumed)
            return sum + (c ?? 0)
          }, 0)

          const lastTextRaw = cleanNumberText(r.rob)
          const lastText = lastTextRaw || cleanNumberText(prev?.last) || ''

          const initialNum = providedInitial ?? prevInitial ?? derivedFromMach ?? prevLast ?? 0

          const computedFromConsumption =
            initialNum !== null && initialNum !== undefined ? initialNum - machConsumedTotal : null

          const derivedLast =
            (machConsumedTotal > 0 && computedFromConsumption !== null
              ? computedFromConsumption
              : null) ??
            parseNumberSafe(lastText) ??
            (computedFromConsumption !== null ? computedFromConsumption : null) ??
            lastMachRob ??
            prevLast ??
            0

          return {
            fieldType: String(r.fuelType ?? '').trim(),
            tankName: String(r.tankName ?? '').trim(),
            q88Grade: String(r.q88Grade ?? '').trim(),
            sulphurPercentage: String(r.sulphurPercentage ?? '').trim(),
            allocationStatus: String(r.allocationStatus ?? '').trim(),
            initialRob: Number(initialNum),
            lastRob: Number(derivedLast),
            machineries: (r.machineries || [])
              .filter((m) => !!String(m.value ?? '').trim())
              .map((m) => ({
                machinery: String(m.value ?? '').trim(),
                operationType: String(m.operationType ?? '').trim(),
                consumed: Number(cleanNumberText(m.consumed) ?? 0) || 0,
                rob: Number(cleanNumberText(m.rob) ?? 0) || 0,
              })),
          }
        })

      const mappedLubeOils = lubeRows
        .filter(
          (r, idx) =>
            String(r.grade ?? '').trim() !== '' ||
            Boolean(customLubeRows[idx]) ||
            String(r.openingStock ?? '').trim() !== '' ||
            String(r.received ?? '').trim() !== '' ||
            String(r.consumedByVessel ?? '').trim() !== '' ||
            String(r.closingStock ?? '').trim() !== ''
        )
        .map((r, idx) => {
          const rawGrade = String(r.grade ?? '').trim()
          const effectiveGrade =
            rawGrade || (customLubeRows[idx] ? 'Others' : '')
          return {
            grade: effectiveGrade,
            openingStock: cleanNumberText(r.openingStock),
            received: cleanNumberText(r.received),
            consumedByVessel: cleanNumberText(r.consumedByVessel),
            closingStock: cleanNumberText(r.closingStock),
          }
        })
        .filter((r) => r.grade !== '')

      const parsedLegId = legId ? Number(legId) : undefined

      return {
        userId: Number.isFinite(parsedUserId) ? parsedUserId : undefined,
        companyId: Number.isFinite(parsedCompanyId) ? parsedCompanyId : undefined,
        voyageId: Number(voyage.id),
        legId: parsedLegId,
        remarks,
        rows: mappedRows,
        lubeOils: isFuelChangeover ? [] : mappedLubeOils,
        tanks: isFuelChangeover ? synthesizedTankWiseRows : undefined,
      }
    }

    // keep ids in sync when template fields arrive
    useEffect(() => {
      setRows((prev) =>
        prev.map((r) => ({
          ...r,
          fuelTypeId: fuelTypeField.id,
          initialRobId: initialRobField.id,
          robId: outerRobField.id,
          machineries: r.machineries.map((m) => ({
            ...m,
            fieldId: machineryField.id,
            consumedId: consumedField.id,
            robId: innerRobField.id,
          })),
        }))
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      fuelTypeField.id,
      initialRobField.id,
      outerRobField.id,
      machineryField.id,
      consumedField.id,
      innerRobField.id,
    ])

    const emitAllToParent = (copy: OuterRow[], isUserInitiated: boolean = false) => {
      const allFuelTypes = copy.map((r) => r.fuelType)
      const allInitialRobs = copy.map((r) => cleanNumberText(r.initialRob))
      const allOuterRobs = copy.map((r) => cleanNumberText(r.rob))
      const allMachinery = copy.map((r) => r.machineries.map((m) => m.value))
      const allOperationTypes = copy.map((r) =>
        r.machineries.map((m) => String(m.operationType ?? ''))
      )
      const allConsumed = copy.map((r) => r.machineries.map((m) => cleanNumberText(m.consumed)))
      const allInnerRobs = copy.map((r) => r.machineries.map((m) => cleanNumberText(m.rob)))

      if (fuelTypeField.id > 0)
        onChange(fuelTypeField.label, fuelTypeField.id, JSON.stringify(allFuelTypes), isUserInitiated)
      if (initialRobField.id > 0)
        onChange(initialRobField.label, initialRobField.id, JSON.stringify(allInitialRobs), isUserInitiated)
      if (outerRobField.id > 0)
        onChange(outerRobField.label, outerRobField.id, JSON.stringify(allOuterRobs), isUserInitiated)
      if (machineryField.id > 0)
        onChange(machineryField.label, machineryField.id, JSON.stringify(allMachinery), isUserInitiated)
      if (operationTypeField.id > 0)
        onChange(operationTypeField.label, operationTypeField.id, JSON.stringify(allOperationTypes), isUserInitiated)
      if (consumedField.id > 0)
        onChange(consumedField.label, consumedField.id, JSON.stringify(allConsumed), isUserInitiated)
      if (innerRobField.id > 0)
        onChange(innerRobField.label, innerRobField.id, JSON.stringify(allInnerRobs), isUserInitiated)
    }

    useEffect(() => {
      if (!hasHydratedRef.current && hasMeaningfulValues(values)) return
      emitAllToParent(rows, isUserModifiedRef.current)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, values])

    useEffect(() => {
      onDtoChange?.(buildDtoFromRows(rows), isUserModifiedRef.current)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, lubeRows, customLubeRows, remarks, voyage?.id, userId, companyId, previousRobMap, onDtoChange, synthesizedTankWiseRows])

    useEffect(() => {
      setOptionsLoading(true)
      const run = async () => {
        try {
          const [fuels, machs] = await Promise.all([
            getConsumptionFuelTypes(token),
            getConsumptionMachineryTypes(token),
          ])
          setFuelOptionsApi(Array.isArray(fuels) ? fuels : [])
          setMachineryOptionsApi(Array.isArray(machs) ? machs : [])
        } catch (e) {
          console.warn('Consumption options API failed', e)
        } finally {
          setOptionsLoading(false)
        }
      }
      run()
    }, [token])

    const hasMeaningfulValues = (vals?: { [fieldId: number]: { value: any; label: string } }) => {
      if (!vals) return false
      return Object.values(vals).some((v) => {
        const val = v?.value
        if (val === undefined || val === null) return false
        if (typeof val === 'string') return val.trim() !== ''
        if (Array.isArray(val)) return val.some((x) => String(x ?? '').trim() !== '')
        return String(val ?? '').trim() !== ''
      })
    }

    const buildRowsFromValues = (vals?: { [fieldId: number]: { value: any; label: string } }): OuterRow[] => {
      if (!vals || !hasMeaningfulValues(vals)) return []
      if (
        fuelTypeField.id <= 0 ||
        outerRobField.id <= 0 ||
        machineryField.id <= 0 ||
        consumedField.id <= 0 ||
        innerRobField.id <= 0
      )
        return []

      const fuelTypes = toStringArray(vals[fuelTypeField.id]?.value)
      const initialRobs =
        initialRobField.id > 0 ? toStringArray(vals[initialRobField.id]?.value) : []
      const outerRobs = toStringArray(vals[outerRobField.id]?.value)
      const machs = toString2D(vals[machineryField.id]?.value)
      const opTypes = operationTypeField.id > 0 ? toString2D(vals[operationTypeField.id]?.value) : []
      const cons = toString2D(vals[consumedField.id]?.value)
      const inner = toString2D(vals[innerRobField.id]?.value)

      const count = Math.max(
        fuelTypes.length,
        initialRobs.length,
        outerRobs.length,
        machs.length,
        0
      )
      if (count === 0) return []

      return Array.from({ length: count }).map((_, i) => {
        const fuel = String(fuelTypes[i] ?? '')
        const init = String(initialRobs[i] ?? '')
        const last = String(outerRobs[i] ?? '0')

        const mArr = machs[i] ?? []
        const oArr = opTypes[i] ?? []
        const cArr = cons[i] ?? []
        const rArr = inner[i] ?? []

        const machineries: MachineryRow[] = (mArr.length ? mArr : ['']).map((m, j) => ({
          fieldId: machineryField.id,
          value: String(m ?? ''),
          operationType: String(oArr[j] ?? ''),
          consumedId: consumedField.id,
          consumed: String(cArr[j] ?? ''),
          robId: innerRobField.id,
          rob: String(rArr[j] ?? ''),
        }))

        const snapRow = snapshot?.rows?.[i]
        const fallbackTank = availableTankOptions[i] || availableTankOptions[0]
        const gradeMatch = COMMON_Q88_FUEL_GRADES.find((g) => g.fuelType === fuel)
        const tankName = snapRow?.tankName || fallbackTank?.name || ''
        const q88Grade =
          snapRow?.q88Grade || gradeMatch?.grade || fallbackTank?.defaultGrade || 'VLSFO 0.5% S (RMG 380)'
        const sulphur = snapRow?.sulphurPercentage || gradeMatch?.sulphur || '0.48%'
        const allocStatus = snapRow?.allocationStatus || fallbackTank?.status || 'Storage Tank'

        return {
          fuelTypeId: fuelTypeField.id,
          fuelType: fuel || snapRow?.fieldType || fallbackTank?.fuelType || 'VLSFO',
          tankName,
          q88Grade,
          sulphurPercentage: sulphur,
          allocationStatus: allocStatus,
          initialRobId: initialRobField.id,
          initialRob: cleanNumberText(init),
          robId: outerRobField.id,
          rob: cleanNumberText(last) || '0',
          remarks: '',
          machineries: machineries.length ? machineries : [makeEmptyRow().machineries[0]],
        }
      })
    }

    // Hydrate from props.values
    useEffect(() => {
      if (hasHydratedRef.current) return
      if (!values || !hasMeaningfulValues(values)) return

      const built = buildRowsFromValues(values)
      if (built.length > 0) {
        setRows(built)
        hasHydratedRef.current = true
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      values,
      fuelTypeField.id,
      initialRobField.id,
      outerRobField.id,
      machineryField.id,
      operationTypeField.id,
      consumedField.id,
      innerRobField.id,
      availableTankOptions,
    ])

    // Load previous ROB seeds for create mode, and full report rows for edit mode
    useEffect(() => {
      const load = async () => {
        if (!voyage?.id) return

        try {
          const parsedReportId = Number(reportId)
          const hasReportId = Number.isFinite(parsedReportId) && parsedReportId > 0
          const vesselId = Number(voyage?.vessel?.id ?? (voyage as any)?.vesselId ?? 0)
          let previousSubmittedData: any = null

          if (vesselId > 0) {
            try {
              previousSubmittedData = await resolvePreviousReportConsumption(
                hasReportId ? parsedReportId : null,
                voyage.id,
                vesselId,
                token
              )
            } catch (err) {
              console.warn('resolvePreviousReportConsumption failed', err)
            }
          }

          if (!previousSubmittedData?.rows?.length) {
            try {
              previousSubmittedData = await getConsumptionByVoyage(token, voyage.id)
            } catch {
              // no record yet
            }
          }

          previousSubmittedDataRef.current = previousSubmittedData
          setPreviousSubmittedData(previousSubmittedData)

          const prevLubeApi = getLubeList(previousSubmittedData)

          const lubeMap: Record<string, string> = {}
          if (Array.isArray(prevLubeApi)) {
            prevLubeApi.forEach((r: any) => {
              const g = norm(r?.grade ?? r?.lubeGrade ?? r?.lube_grade)
              if (!g) return
              lubeMap[g] =
                cleanNumberText(r?.closingStock ?? r?.closing_stock ?? '') ||
                cleanNumberText(r?.openingStock ?? r?.opening_stock ?? '')
            })
          }
          setPreviousLubeMap(lubeMap)

          const map: Record<string, { initial: string; last: string }> = {}
          const discoveredFuelTypes: Set<string> = new Set()
          if (previousSubmittedData?.rows?.length) {
            previousSubmittedData.rows.forEach((r: any) => {
              const ftRaw = String(r?.fuelType || r?.fieldType || r?.q88Grade || r?.tankName || '').trim()
              const ftKey = normFuelKey(ftRaw)
              const tankKey = normFuelKey(r?.tankName)

              const machList = Array.isArray(r?.machineries) ? r.machineries : []
              const lastFromMachinery = machList.length ? machList[machList.length - 1]?.rob : undefined

              const last = firstNonEmptyNumberText(
                r?.lastRob,
                r?.rob,
                lastFromMachinery ?? '',
                r?.last_rob,
                ''
              )
              const initial = firstNonEmptyNumberText(
                r?.initialRob,
                r?.initial_rob,
                last,
                lastFromMachinery,
                ''
              )

              const entry = {
                initial: initial || last,
                last: last || initial,
              }

              if (ftKey) {
                map[ftKey] = entry
                discoveredFuelTypes.add(ftRaw)
              }
              if (tankKey) {
                map[tankKey] = entry
              }
            })
            setPreviousRobMap(map)

            if (discoveredFuelTypes.size) {
              setFuelOptionsApi((prev) => {
                const merged = new Set(prev)
                discoveredFuelTypes.forEach((f) => merged.add(f))
                return Array.from(merged)
              })
            }
          }

          // Case 1: Returning from Preview
          if (hasMeaningfulSnapshot(snapshot) && !hasReportId) {
            if (!isUserModifiedRef.current) {
              setRemarks(typeof snapshot?.remarks === 'string' ? snapshot.remarks : '')
              setRows(buildRowsFromDto(snapshot))
              const snapshotLubes = getLubeList(snapshot)
              if (snapshotLubes.length > 0) {
                applyLubeRows(snapshotLubes)
              } else {
                setLubeRows([blankLubeOilRow()])
                setCustomLubeRows({})
              }
            }
            hasHydratedRef.current = true
            return
          }

          // Case 2: Create Mode (new report)
          if (!hasReportId) {
            if (!isUserModifiedRef.current) {
              const prevRemarks =
                isFuelChangeover && typeof previousSubmittedData?.remarks === 'string'
                  ? previousSubmittedData.remarks
                  : ''
              setRemarks(prevRemarks)
              if (isFuelChangeover && previousSubmittedData?.rows?.length) {
                setRows(mapApiRowsToUiRows(previousSubmittedData.rows))
              } else {
                setRows([makeEmptyRow()])
              }
              const prevLubes = getLubeList(previousSubmittedData)
              if (isFuelChangeover && prevLubes.length > 0) {
                applyLubeRows(prevLubes)
              } else {
                setLubeRows([blankLubeOilRow()])
                setCustomLubeRows({})
              }
            }
            hasHydratedRef.current = true
            return
          }

          // Case 3: Edit Mode (existing report)
          if (isUserModifiedRef.current) {
            hasHydratedRef.current = true
            return
          }

          let editData: any = null
          try {
            editData = await getConsumptionByVoyageReport(voyage.id, parsedReportId)
          } catch {
            // fallback
          }

          let currentRows: OuterRow[] = []
          if (editData?.rows?.length) {
            currentRows = mapApiRowsToUiRows(editData.rows)
          } else if (hasMeaningfulSnapshot(snapshot)) {
            currentRows = buildRowsFromDto(snapshot)
          } else if (hasMeaningfulValues(values)) {
            currentRows = buildRowsFromValues(values)
          }

          const carryForwardRows = previousSubmittedData?.rows?.length
            ? mapApiRowsToUiRows(previousSubmittedData.rows)
            : []
          const mergedRows = mergeRowsWithCarryForward(currentRows, carryForwardRows)

          if (editData) {
            const r = editData.remarks ?? editData.remark
            if (typeof r === 'string') {
              setRemarks(r)
            }
          }

          if (currentRows.length || editData?.rows?.length || previousSubmittedData?.rows?.length) {
            setRows(mergedRows)
            hasHydratedRef.current = true
          }

          const editLubes = getLubeList(editData)
          const snapshotLubes = getLubeList(snapshot)
          const prevLubes = getLubeList(previousSubmittedData)

          if (editLubes.length > 0) {
            applyLubeRows(editLubes)
          } else if (snapshotLubes.length > 0) {
            applyLubeRows(snapshotLubes)
          } else if (prevLubes.length > 0) {
            applyLubeRows(prevLubes)
          } else {
            setLubeRows([blankLubeOilRow()])
            setCustomLubeRows({})
          }
        } catch (e) {
          // no record yet
        }
      }

      load()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voyage?.id, reportId, fuelTypeField.id, initialRobField.id, outerRobField.id, machineryField.id, consumedField.id, innerRobField.id, refreshFlag])

    useImperativeHandle(
      ref,
      () => ({
        getPayload: () => {
          const allFuelTypes = rows.map((r) => r.fuelType)
          const allInitialRobs = rows.map((r) => cleanNumberText(r.initialRob))
          const allOuterRobs = rows.map((r) => cleanNumberText(r.rob))
          const allMachinery = rows.map((r) => r.machineries.map((m) => m.value))
          const allOperationTypes = rows.map((r) =>
            r.machineries.map((m) => String(m.operationType ?? ''))
          )
          const allConsumed = rows.map((r) => r.machineries.map((m) => cleanNumberText(m.consumed)))
          const allInnerRobs = rows.map((r) => r.machineries.map((m) => cleanNumberText(m.rob)))

          const payload: { field: number; valueText: string }[] = []
          if (fuelTypeField.id > 0)
            payload.push({ field: fuelTypeField.id, valueText: JSON.stringify(allFuelTypes) })
          if (initialRobField.id > 0)
            payload.push({ field: initialRobField.id, valueText: JSON.stringify(allInitialRobs) })
          if (outerRobField.id > 0)
            payload.push({ field: outerRobField.id, valueText: JSON.stringify(allOuterRobs) })
          if (machineryField.id > 0)
            payload.push({ field: machineryField.id, valueText: JSON.stringify(allMachinery) })
          if (operationTypeField.id > 0)
            payload.push({ field: operationTypeField.id, valueText: JSON.stringify(allOperationTypes) })
          if (consumedField.id > 0)
            payload.push({ field: consumedField.id, valueText: JSON.stringify(allConsumed) })
          if (innerRobField.id > 0)
            payload.push({ field: innerRobField.id, valueText: JSON.stringify(allInnerRobs) })
          return payload
        },

        getDto: () => buildDtoFromRows(rows),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [remarks, rows, lubeRows, customLubeRows, voyage?.id, legId, userId, companyId, previousRobMap, synthesizedTankWiseRows]
    )

    const removeOuterRow = (outerIndex: number) => {
      isUserModifiedRef.current = true
      setRows((rs) => {
        if (rs.length <= 1) return rs
        const next = rs.filter((_, idx) => idx !== outerIndex)
        return next
      })
    }

    const resolveInitialRobForFuel = (
      fuelName: string,
      outerIndex: number,
      currentRows: OuterRow[],
      tankName?: string
    ): { initialRob: string; lastRob: string } => {
      const currentFuelKey = normFuelKey(fuelName)
      const currentTankKey = normFuelKey(tankName)

      // 1. If tankName is provided, check previousRobMap for this specific tank
      if (currentTankKey && previousRobMap[currentTankKey]) {
        const prev = previousRobMap[currentTankKey]
        const lastRob = cleanNumberText(prev?.last ?? prev?.initial ?? '')
        const initialFromPrev = cleanNumberText(prev?.initial ?? lastRob)
        return {
          initialRob: initialFromPrev,
          lastRob: lastRob || initialFromPrev,
        }
      }

      // 2. If tankName is provided, look for a prior row in currentRows with the SAME tank
      if (currentTankKey) {
        const priorSameTank = [...currentRows]
          .slice(0, outerIndex)
          .reverse()
          .find(
            (r) => normFuelKey(r.tankName) === currentTankKey && String(r.rob ?? '').trim() !== ''
          )
        if (priorSameTank) {
          const lastRob = cleanNumberText(priorSameTank.rob)
          return { initialRob: lastRob, lastRob }
        }
      }

      // 3. If no tankName is specified, look up by fuelType as fallback
      if (currentFuelKey && !currentTankKey) {
        const priorSameFuel = [...currentRows]
          .slice(0, outerIndex)
          .reverse()
          .find(
            (r) =>
              !normFuelKey(r.tankName) &&
              normFuelKey(r.fuelType) === currentFuelKey &&
              String(r.rob ?? '').trim() !== ''
          )

        let prev = previousRobMap[currentFuelKey]
        if (!prev) {
          for (const [k, v] of Object.entries(previousRobMap)) {
            if (k.includes(currentFuelKey) || currentFuelKey.includes(k)) {
              prev = v
              break
            }
          }
        }

        const lastRob = cleanNumberText(priorSameFuel?.rob ?? prev?.last ?? '')
        const initialFromPrev = lastRob || cleanNumberText(prev?.initial ?? '')

        return {
          initialRob: initialFromPrev,
          lastRob: lastRob || initialFromPrev,
        }
      }

      return { initialRob: '', lastRob: '' }
    }

    const addOuterRow = () => {
      isUserModifiedRef.current = true
      setRows((rs) => {
        const nextTank = availableTankOptions[rs.length % availableTankOptions.length]
        const newRow = makeEmptyRow(nextTank)
        if (newRow.fuelType) {
          const { initialRob, lastRob } = resolveInitialRobForFuel(
            newRow.fuelType,
            rs.length,
            rs,
            newRow.tankName
          )
          newRow.initialRob = initialRob
          const computedRow = applyConsumptionMath(newRow, lastRob)
          return [...rs, computedRow]
        }
        return [...rs, newRow]
      })
    }

    const addInnerRow = (outerIndex: number) => {
      isUserModifiedRef.current = true
      setRows((rs) => {
        const copy = [...rs]
        copy[outerIndex] = {
          ...copy[outerIndex],
          machineries: [
            ...copy[outerIndex].machineries,
            {
              fieldId: machineryField.id,
              value: '',
              operationType: '',
              remarks: '',
              consumedId: consumedField.id,
              consumed: '',
              robId: innerRobField.id,
              rob: '',
            },
          ],
        }
        return copy
      })
    }

    const removeInnerRow = (outerIndex: number, innerIndex: number) => {
      isUserModifiedRef.current = true
      setRows((rs) => {
        const copy = [...rs]
        if (copy[outerIndex].machineries.length > 1) {
          copy[outerIndex] = {
            ...copy[outerIndex],
            machineries: copy[outerIndex].machineries.filter((_, i) => i !== innerIndex),
          }
        }
        return copy
      })
    }

    const parseNumberSafe = (text: any): number | null => {
      const clean = cleanNumberText(text)
      if (clean === '') return null
      const num = parseFloat(clean)
      return Number.isFinite(num) ? num : null
    }

    const deriveBaseRob = (row: OuterRow, prevLastText?: string) => {
      const baseFromInitial = parseNumberSafe(row.initialRob)
      if (baseFromInitial !== null) return baseFromInitial

      const baseFromPrevLast = parseNumberSafe(prevLastText)
      if (baseFromPrevLast !== null) return baseFromPrevLast

      return 0
    }

    const applyConsumptionMath = (row: OuterRow, prevLastText?: string): OuterRow => {
      const baseRob = deriveBaseRob(row, prevLastText)

      const hasInitial = String(row.initialRob ?? '').trim() !== ''
      const hasPrev = String(prevLastText ?? '').trim() !== ''
      const hasConsumed = row.machineries.some((m) => String(m.consumed ?? '').trim() !== '')

      let runningRob = baseRob
      const updatedMachineries = row.machineries.map((mach) => {
        const consumedNum = parseNumberSafe(mach.consumed) ?? 0
        if (hasInitial || hasPrev || hasConsumed) {
          runningRob = runningRob - consumedNum
        }
        return {
          ...mach,
          rob: (hasInitial || hasPrev || hasConsumed) && Number.isFinite(runningRob)
            ? capDecimalsToTwo(runningRob)
            : '',
        }
      })

      const displayRob = (hasInitial || hasPrev || hasConsumed) && Number.isFinite(runningRob)
        ? capDecimalsToTwo(runningRob)
        : ''

      return {
        ...row,
        machineries: updatedMachineries,
        rob: displayRob,
      }
    }

    const computeTotalConsumed = (row: OuterRow): string => {
      const total = row.machineries.reduce((sum, mach) => {
        const consumedNum = parseNumberSafe(mach.consumed) ?? 0
        return sum + consumedNum
      }, 0)
      return capDecimalsToTwo(total)
    }

    const recalculateFuelChain = (
      rowsCopy: OuterRow[],
      fuelName: string,
      startIndex: number,
      targetTankName?: string
    ) => {
      const key = fuelKey(fuelName)
      if (!key) return
      let previousLastText: string | undefined

      const targetTank = norm(targetTankName)

      for (let i = 0; i < rowsCopy.length; i++) {
        const row = rowsCopy[i]
        if (fuelKey(row.fuelType) !== key) continue

        const rowTank = norm(row.tankName)
        // If tank names exist and are different, they are different physical tanks and must NOT chain!
        if (targetTank && rowTank && rowTank !== targetTank) {
          continue
        }

        if (i < startIndex) {
          previousLastText = String(row.rob ?? '').trim() || previousLastText
          continue
        }

        const isSameTankSequence = !targetTank || rowTank === targetTank
        const chainedRow =
          previousLastText !== undefined && previousLastText !== '' && isSameTankSequence
            ? { ...row, initialRob: previousLastText }
            : row
        const recomputed = applyConsumptionMath(chainedRow, previousLastText)
        rowsCopy[i] = recomputed
        previousLastText = recomputed.rob
      }
    }

    const updateOuter = (
      outerIndex: number,
      key: 'fuelType' | 'rob' | 'initialRob' | 'tankName' | 'q88Grade' | 'allocationStatus' | 'sulphurPercentage',
      value: string
    ) => {
      isUserModifiedRef.current = true
      setRows((rs) => {
        const copy = [...rs]
        const oldFuelType = copy[outerIndex].fuelType
        const typedValue = key === 'initialRob' ? clampTypedToTwoDecimals(value) : value
        let row = { ...copy[outerIndex], [key]: cleanNumberText(typedValue) } as OuterRow

        if (key === 'fuelType') {
          row.fuelType = value
          const { initialRob, lastRob } = resolveInitialRobForFuel(value, outerIndex, copy, row.tankName)
          row.initialRob = initialRob
          row = applyConsumptionMath(row, lastRob)
        } else if (key === 'q88Grade') {
          const match = COMMON_Q88_FUEL_GRADES.find((g) => g.grade === value)
          if (match) {
            row.q88Grade = match.grade
            if (match.fuelType) {
              row.fuelType = match.fuelType
              const { initialRob, lastRob } = resolveInitialRobForFuel(match.fuelType, outerIndex, copy, row.tankName)
              row.initialRob = initialRob
              row = applyConsumptionMath(row, lastRob)
            }
            if (match.sulphur) row.sulphurPercentage = match.sulphur
          }
        } else if (key === 'tankName') {
          row.tankName = value
          const matchedTank = availableTankOptions.find((t) => t.name === value)
          if (matchedTank) {
            if (!row.q88Grade || row.q88Grade === 'Others') {
              row.q88Grade = matchedTank.defaultGrade
              const gradeMatch = COMMON_Q88_FUEL_GRADES.find((g) => g.grade === matchedTank.defaultGrade)
              if (gradeMatch) {
                row.fuelType = gradeMatch.fuelType
                row.sulphurPercentage = gradeMatch.sulphur
                const { initialRob, lastRob } = resolveInitialRobForFuel(gradeMatch.fuelType, outerIndex, copy, value)
                row.initialRob = initialRob
                row = applyConsumptionMath(row, lastRob)
              }
            }
            if (!row.allocationStatus || row.allocationStatus === 'Storage Tank') {
              row.allocationStatus = matchedTank.status
            }
          }
        } else if (key === 'initialRob') {
          row = applyConsumptionMath(row)
        }

        copy[outerIndex] = row
        recalculateFuelChain(copy, row.fuelType, outerIndex + 1, row.tankName)
        if (oldFuelType && fuelKey(oldFuelType) !== fuelKey(row.fuelType)) {
          recalculateFuelChain(copy, oldFuelType, 0, copy[outerIndex].tankName)
        }
        return copy
      })
    }

    const updateInner = (
      outerIndex: number,
      innerIndex: number,
      key: 'value' | 'operationType' | 'consumed' | 'rob',
      value: string
    ) => {
      isUserModifiedRef.current = true
      setRows((rs) => {
        const copy = [...rs]
        const row = copy[outerIndex]
        const mach = row.machineries[innerIndex]

        const updatedMach: MachineryRow = {
          ...mach,
          [key]:
            key === 'value' || key === 'operationType'
              ? value
              : cleanNumberText(key === 'consumed' ? clampTypedToTwoDecimals(value) : value),
        } as MachineryRow

        if (key === 'value' && getOperationOptions(value).length === 0) {
          updatedMach.operationType = ''
        }

        const newMachineries = row.machineries.map((m, i) => (i === innerIndex ? updatedMach : m))

        copy[outerIndex] = applyConsumptionMath({ ...row, machineries: newMachineries })
        recalculateFuelChain(copy, copy[outerIndex].fuelType, outerIndex + 1, copy[outerIndex].tankName)
        return copy
      })
    }

    const shimmerKeyframes = `
      @keyframes shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }
    `

    const shimmerBaseStyle: React.CSSProperties = {
      background: 'linear-gradient(90deg, #f5f7fb 0%, #eef1f6 50%, #f5f7fb 100%)',
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.3s ease-in-out infinite',
      borderRadius: '8px',
    }

    const renderLoaderRow = () => (
      <div className='border rounded p-5 mb-4'>
        <div className='d-flex align-items-center gap-2 mb-3'>
          <div style={{ ...shimmerBaseStyle, height: '38px', width: '40px' }} />
          <div style={{ ...shimmerBaseStyle, height: '38px', width: '160px' }} />
          <div style={{ ...shimmerBaseStyle, height: '38px', width: '140px' }} />
          <div style={{ ...shimmerBaseStyle, height: '38px', width: '120px' }} />
          <div style={{ ...shimmerBaseStyle, height: '38px', width: '60px' }} />
        </div>
      </div>
    )

    const getOperationOptions = (machineryName: string) => {
      const name = norm(machineryName)
      const compactName = name.replace(/[\s-]+/g, '')

      if (compactName === 'boiler1' || compactName === 'boiler2') {
        return ['Interting and heating', 'Discharge operations', 'Routine Consumption']
      }
      if (['ae1', 'ae2', 'ae3', 'ae4'].includes(compactName)) {
        return [
          'Routine Consumption',
          'Consumption for discharge pumps',
          'Interting and heating',
          'Discharge operations',
        ]
      }
      return []
    }

    return (
      <>
        <style>{shimmerKeyframes}</style>
        <div className='px-3'>
          <h4 className='fw-bold text-dark py-3'>{sectionTitle}</h4>

          {optionsLoading ? (
            <>
              {renderLoaderRow()}
              {renderLoaderRow()}
            </>
          ) : isFuelChangeover ? (
            /* ──────────────── Fuel Changeover: Read-Only Tank-Wise & Q88 Telemetry View ──────────────── */
            <div>
              <div className='alert alert-primary d-flex align-items-center mb-4 p-4 shadow-sm border border-primary border-opacity-25'>
                <div className='d-flex flex-column'>
                  <h5 className='mb-1 fw-bold text-primary'>
                    Tank-Wise Information &amp; Q88 Grade Telemetry (Read-Only)
                  </h5>
                  <span className='text-muted small'>
                    Automatically populated from submitted position reports of this voyage. Display only for ECA Changeover verification.
                  </span>
                </div>
              </div>

              {/* 4 KPI Summary Cards */}
              <div className='row g-4 mb-5'>
                <div className='col-md-3'>
                  <div className='card bg-light-primary border border-primary border-opacity-25 shadow-sm p-4 h-100'>
                    <div className='text-muted small fw-bold text-uppercase'>Total VLSFO ROB</div>
                    <div className='fs-2hx fw-bolder text-primary my-1'>{changeoverKpis.totalVlsfo.toFixed(2)} MT</div>
                    <div className='text-muted fs-7'>Pre-changeover HFO/VLSFO Service &amp; Settling</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card bg-light-success border border-success border-opacity-25 shadow-sm p-4 h-100'>
                    <div className='text-muted small fw-bold text-uppercase'>Total LSMGO ROB (ECA)</div>
                    <div className='fs-2hx fw-bolder text-success my-1'>{changeoverKpis.totalLsmgo.toFixed(2)} MT</div>
                    <div className='text-muted fs-7'>ECA Compliant Low Sulphur MGO Fuel</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card bg-light-info border border-info border-opacity-25 shadow-sm p-4 h-100'>
                    <div className='text-muted small fw-bold text-uppercase'>Total Consumed / Transferred</div>
                    <div className='fs-2hx fw-bolder text-info my-1'>{changeoverKpis.totalConsumed.toFixed(2)} MT</div>
                    <div className='text-muted fs-7'>Voyage Consumption &amp; Changeover Flushing</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className={`card ${changeoverKpis.isEcaCompliant ? 'bg-light-success border-success' : 'bg-light-warning border-warning'} border border-opacity-25 shadow-sm p-4 h-100`}>
                    <div className='text-muted small fw-bold text-uppercase'>ECA Changeover Status</div>
                    <div className='my-1'>
                      {changeoverKpis.isEcaCompliant ? (
                        <span className='badge badge-success fs-7 fw-bold px-3 py-2'>COMPLIANT (≤ 0.10% S)</span>
                      ) : changeoverKpis.hasData ? (
                        <span className='badge badge-warning fs-7 fw-bold px-3 py-2'>PRE-CHANGEOVER ({changeoverKpis.activeServiceTankSulphur})</span>
                      ) : (
                        <span className='badge badge-secondary fs-7 fw-bold px-3 py-2'>PENDING TELEMETRY</span>
                      )}
                    </div>
                    <div className='text-muted fs-7'>
                      {changeoverKpis.hasData
                        ? `Active Service Tank Sulphur ${changeoverKpis.activeServiceTankSulphur}`
                        : 'No position reports submitted for this voyage'}
                    </div>
                  </div>
                </div>
              </div>

              {/* 9-column Tank-Wise Table */}
              <div className='card border mb-5 shadow-sm'>
                <div className='card-header py-3 bg-light'>
                  <h5 className='card-title fw-bold text-dark m-0'>Tank-Wise Fuel Allocation &amp; Q88 Grades</h5>
                </div>
                <div className='card-body p-0 table-responsive'>
                  <table className='table table-bordered table-hover align-middle mb-0 text-center fs-7'>
                    <thead className='table-dark text-uppercase'>
                      <tr>
                        <th style={{ width: '40px' }}>#</th>
                        <th style={{ minWidth: '180px' }} className='text-start'>TANK NAME</th>
                        <th style={{ minWidth: '170px' }} className='text-start'>Q88 GRADE</th>
                        <th style={{ minWidth: '90px' }}>FUEL TYPE</th>
                        <th style={{ minWidth: '110px' }}>SOUNDING ROB (MT)</th>
                        <th style={{ minWidth: '120px' }}>CONSUMED / TRANSFERRED (MT)</th>
                        <th style={{ minWidth: '110px' }}>CURRENT ROB (MT)</th>
                        <th style={{ minWidth: '90px' }}>SULPHUR %</th>
                        <th style={{ minWidth: '160px' }}>ALLOCATION STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {synthesizedTankWiseRows.length === 0 ? (
                        <tr>
                          <td colSpan={9} className='py-6 text-center text-muted fs-6'>
                            No tank-wise position reports submitted yet for this voyage. Telemetry will appear once reports are submitted.
                          </td>
                        </tr>
                      ) : (
                        synthesizedTankWiseRows.map((tRow, tIdx) => (
                          <tr key={tIdx}>
                            <td className='fw-bold text-muted'>{tIdx + 1}</td>
                            <td className='text-start fw-semibold text-dark'>{tRow.tankName}</td>
                            <td className='text-start text-primary fw-semibold'>{tRow.q88Grade}</td>
                            <td><span className='badge badge-light-primary fw-bold'>{tRow.fuelType}</span></td>
                            <td className='text-end fw-semibold pe-3'>{Number(tRow.soundingRob).toFixed(2)}</td>
                            <td className='text-end fw-semibold pe-3 text-danger'>{Number(tRow.consumedTransferred) > 0 ? Number(tRow.consumedTransferred).toFixed(2) : '0.00'}</td>
                            <td className='text-end fw-bolder pe-3 text-success'>{Number(tRow.currentRob).toFixed(2)}</td>
                            <td><span className='badge badge-light-info fw-bold'>{tRow.sulphurPercentage}</span></td>
                            <td>
                              <span className={`badge ${tRow.allocationStatus.includes('Active') ? 'badge-light-success' :
                                  tRow.allocationStatus.includes('In Service') ? 'badge-light-warning' :
                                    tRow.allocationStatus.includes('ECA') ? 'badge-light-info' : 'badge-light-secondary'
                                } fw-bold`}>
                                {tRow.allocationStatus}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            /* ──────────────── Standard Position Reports: Fuel Entry with Tank & Q88 Selection ──────────────── */
            rows.map((row, oi) => (
              <div key={oi} className='border rounded p-4 mb-4 shadow-sm bg-white'>
                <div className='d-flex align-items-start gap-2 mb-3'>
                  <button
                    type='button'
                    className='btn btn-action btn-action-primary mt-4'
                    onClick={addOuterRow}
                    title='Add Fuel / Tank Block'
                  >
                    +
                  </button>

                  <div className='flex-fill'>
                    <div className='small text-muted mb-1 fw-semibold'>Tank Name</div>
                    <SearchableSelect
                      className='form-select'
                      value={row.tankName}
                      onChange={(e) => updateOuter(oi, 'tankName', e.target.value)}
                    >
                      <option value=''>Select Tank</option>
                      {availableTankOptions.map((opt) => (
                        <option key={opt.name} value={opt.name}>
                          {opt.name}
                        </option>
                      ))}
                      <option value='Others'>Others</option>
                    </SearchableSelect>
                  </div>

                  <div className='flex-fill'>
                    <div className='small text-muted mb-1 fw-semibold'>Q88 Fuel Grade</div>
                    <SearchableSelect
                      className='form-select'
                      value={row.q88Grade}
                      onChange={(e) => updateOuter(oi, 'q88Grade', e.target.value)}
                    >
                      <option value=''>Select Q88 Grade</option>
                      {COMMON_Q88_FUEL_GRADES.map((opt) => (
                        <option key={opt.grade} value={opt.grade}>
                          {opt.grade}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>

                  <div className='flex-fill'>
                    <div className='small text-muted mb-1 fw-semibold'>Fuel Type</div>
                    <SearchableSelect
                      className='form-select'
                      value={row.fuelType}
                      onChange={(e) => updateOuter(oi, 'fuelType', e.target.value)}
                    >
                      <option value=''>Select Fuel Type</option>
                      {fuelOptions.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>

                  <div style={{ width: '110px' }}>
                    <div className='small text-muted mb-1 fw-semibold'>Sulphur %</div>
                    <input
                      type='text'
                      className='form-control text-center'
                      placeholder='e.g. 0.48%'
                      value={row.sulphurPercentage}
                      onChange={(e) => updateOuter(oi, 'sulphurPercentage', e.target.value)}
                    />
                  </div>

                  <div className='flex-fill'>
                    <div className='small text-muted mb-1 fw-semibold'>Allocation Status</div>
                    <SearchableSelect
                      className='form-select'
                      value={row.allocationStatus}
                      onChange={(e) => updateOuter(oi, 'allocationStatus', e.target.value)}
                    >
                      {TANK_ALLOCATION_STATUS_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>

                  <button
                    type='button'
                    className='btn btn-action btn-action-danger mt-4'
                    onClick={() => removeOuterRow(oi)}
                    disabled={rows.length === 1}
                    title='Remove Fuel Block'
                  >
                    -
                  </button>
                </div>

                <div className='row g-3 px-2 mb-3 bg-light rounded py-2'>
                  <div className='col-md-6'>
                    <div className='small text-muted mb-1 fw-semibold'>Opening / Initial ROB (MT)</div>
                    <input
                      type='number'
                      step='any'
                      inputMode='decimal'
                      className='form-control'
                      placeholder='Enter initial ROB'
                      value={row.initialRob}
                      onChange={(e) => updateOuter(oi, 'initialRob', e.target.value)}
                    />
                  </div>
                  <div className='col-md-6'>
                    <div className='small text-muted mb-1 fw-semibold'>Current / Last ROB (MT)</div>
                    <input
                      type='number'
                      step='any'
                      inputMode='decimal'
                      className='form-control bg-light'
                      placeholder='Auto calculated'
                      value={row.rob}
                      readOnly
                    />
                  </div>
                </div>

                {row.machineries.map((mach, ii) => (
                  <div key={ii} className='d-flex align-items-start gap-2 ms-4 mb-2'>
                    <button
                      type='button'
                      className='btn btn-action-sm btn-action-secondary-primary'
                      onClick={() => addInnerRow(oi)}
                      title='Add Machinery Row'
                    >
                      +
                    </button>

                    <div className='flex-fill'>
                      <div className='small text-muted mb-1 fw-semibold'>Machinery</div>
                      <SearchableSelect
                        className='form-select'
                        value={mach.value}
                        onChange={(e) => updateInner(oi, ii, 'value', e.target.value)}
                      >
                        <option value=''>Select Machinery</option>
                        {machineryOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </SearchableSelect>
                    </div>

                    {getOperationOptions(mach.value).length > 0 && (
                      <div className='flex-fill'>
                        <div className='small text-muted mb-1 fw-semibold'>Operation Type</div>
                        <SearchableSelect
                          className='form-select'
                          value={mach.operationType ?? ''}
                          onChange={(e) => updateInner(oi, ii, 'operationType', e.target.value)}
                        >
                          <option value=''>Select Operation Type</option>
                          {getOperationOptions(mach.value).map((opt) => (
                            <option key={opt} value={opt}>
                              {opt}
                            </option>
                          ))}
                        </SearchableSelect>
                      </div>
                    )}

                    <div className='flex-fill'>
                      <div className='small text-muted mb-1 fw-semibold'>Consumed (MT)</div>
                      <input
                        type='number'
                        step='any'
                        inputMode='decimal'
                        className='form-control'
                        placeholder='Enter consumed'
                        value={mach.consumed}
                        onChange={(e) => updateInner(oi, ii, 'consumed', e.target.value)}
                      />
                    </div>

                    <div className='flex-fill'>
                      <div className='small text-muted mb-1 fw-semibold'>Machinery ROB (MT)</div>
                      <input
                        type='number'
                        step='any'
                        inputMode='decimal'
                        className='form-control'
                        placeholder='Auto calculated'
                        value={mach.rob}
                        readOnly
                      />
                    </div>

                    <button
                      type='button'
                      className='btn btn-action-sm btn-action-secondary-danger'
                      onClick={() => removeInnerRow(oi, ii)}
                      disabled={row.machineries.length === 1}
                      title='Remove Machinery Row'
                    >
                      -
                    </button>
                  </div>
                ))}

                <div className='d-flex justify-content-end ms-4 mt-2'>
                  <div className='text-end'>
                    <span className='small text-muted fw-semibold me-2'>
                      Total Consumption{row.fuelType ? ` (${row.fuelType})` : ''}:
                    </span>
                    <span className='fw-bold'>{computeTotalConsumed(row)} MT</span>
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Lube Oil Status Table */}
          {!isFuelChangeover && (
            <div className='border rounded p-4 mb-4 bg-white shadow-sm'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <h5 className='fw-bold text-dark m-0'>Lube Oil Status</h5>
                <button type='button' className='btn btn-sm btn-primary' onClick={addLubeRow}>
                  + Add Row
                </button>
              </div>
              <div className='table-responsive'>
                <table className='table table-bordered table-sm align-middle text-center'>
                  <thead className='table-light'>
                    <tr>
                      <th style={{ width: '70px' }}>Sr. No</th>
                      <th style={{ minWidth: '200px' }}>Grades</th>
                      <th style={{ width: '150px' }}>Opening Stock</th>
                      <th style={{ width: '130px' }}>Received</th>
                      <th style={{ width: '160px' }}>Consumed by Vessel</th>
                      <th style={{ width: '150px' }}>Closing Stock</th>
                      <th style={{ width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lubeRows.map((row, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>
                          {customLubeRows[idx] ? (
                            <div className='input-group input-group-sm'>
                              <input
                                type='text'
                                className='form-control form-control-sm'
                                placeholder='Enter Grade'
                                value={row.grade}
                                onChange={(e) => handleLubeChange(idx, 'grade', e.target.value)}
                                autoFocus
                              />
                              <button
                                type='button'
                                className='btn btn-outline-secondary px-2'
                                title='Select from List'
                                onClick={() => {
                                  setCustomLubeRows((prev) => {
                                    const next = { ...prev }
                                    delete next[idx]
                                    return next
                                  })
                                  handleLubeChange(idx, 'grade', '')
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <SearchableSelect
                              className='form-select form-select-sm'
                              value={row.grade}
                              onChange={(e) => {
                                const val = String(e.target.value ?? '').trim()
                                if (val.toLowerCase() === 'others' || val.toLowerCase() === 'other') {
                                  setCustomLubeRows((prev) => ({ ...prev, [idx]: true }))
                                  handleLubeChange(idx, 'grade', 'Others')
                                } else {
                                  handleLubeChange(idx, 'grade', e.target.value)
                                }
                              }}
                            >
                              <option value=''>Select Grade</option>
                              {getLubeGradeOptionsForRow(lubeRows, idx).map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                              <option value='Others'>Others</option>
                              <option value='Other'>Other</option>
                            </SearchableSelect>
                          )}
                        </td>
                        <td>
                          <input
                            type='text'
                            className='form-control form-control-sm text-end'
                            placeholder='0.00'
                            value={row.openingStock}
                            onChange={(e) => handleLubeChange(idx, 'openingStock', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type='text'
                            className='form-control form-control-sm text-end'
                            placeholder='0.00'
                            value={row.received}
                            onChange={(e) => handleLubeChange(idx, 'received', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type='text'
                            className='form-control form-control-sm text-end'
                            placeholder='0.00'
                            value={row.consumedByVessel}
                            onChange={(e) => handleLubeChange(idx, 'consumedByVessel', e.target.value)}
                          />
                        </td>
                        <td>
                          <span className='fw-bold'>{row.closingStock || '0.00'}</span>
                        </td>
                        <td>
                          <button
                            type='button'
                            className='btn btn-action-sm btn-action-secondary-danger'
                            onClick={() => removeLubeRow(idx)}
                            disabled={lubeRows.length === 1}
                          >
                            -
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Remarks Card */}
          <div className='border rounded p-4 mb-4 bg-white shadow-sm'>
            <div className='small text-muted mb-1 fw-semibold'>Remarks</div>
            <input
              type='text'
              className={`form-control ${isFuelChangeover ? 'bg-light' : ''}`}
              placeholder='Enter remarks for this report'
              value={remarks}
              readOnly={isFuelChangeover}
              onChange={(e) => {
                if (isFuelChangeover) return
                isUserModifiedRef.current = true
                setRemarks(e.target.value)
              }}
            />
          </div>
        </div>
      </>
    )
  }
)

export default AddConsumptionROB
